import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper to extract only essential cart data (no large base64 images)
const getCartItemData = (product) => {
  // Only store first image, and if it's a base64 image, skip it
  let thumbnailImage = null;
  if (product.images && product.images.length > 0) {
    const firstImage = product.images[0];
    // Skip base64 images to avoid localStorage quota issues
    if (!firstImage.startsWith('data:')) {
      thumbnailImage = firstImage;
    }
  }

  return {
    id: product.id,
    name: product.name,
    category_name: product.category_name || '',
    price: product.price,
    discount: product.discount || 0,
    final_price: product.final_price,
    thumbnail: thumbnailImage, // Only store one small image reference
  };
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [shopSlug, setShopSlug] = useState(null);

  useEffect(() => {
    // Load cart from localStorage
    const storedCart = localStorage.getItem('cart');
    const storedShop = localStorage.getItem('cartShop');
    
    if (storedCart) {
      try {
        setCart(JSON.parse(storedCart));
      } catch (e) {
        localStorage.removeItem('cart');
      }
    }
    if (storedShop) {
      setShopSlug(storedShop);
    }
  }, []);

  const saveCart = (newCart, shop) => {
    try {
      localStorage.setItem('cart', JSON.stringify(newCart));
      if (shop) {
        localStorage.setItem('cartShop', shop);
        setShopSlug(shop);
      }
    } catch (e) {
      // If localStorage is full, clear old data and try again
      console.warn('localStorage quota exceeded, clearing old cart data');
      localStorage.removeItem('cart');
      try {
        localStorage.setItem('cart', JSON.stringify(newCart));
      } catch (e2) {
        console.error('Failed to save cart even after clearing');
      }
    }
    setCart(newCart);
  };

  const addToCart = (product, quantity = 1, shop) => {
    // Extract only essential data to avoid localStorage quota issues
    const cartItem = {
      ...getCartItemData(product),
      quantity
    };

    // If cart is from different shop, clear it
    if (shopSlug && shopSlug !== shop) {
      saveCart([cartItem], shop);
      return;
    }

    const existingIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += quantity;
      saveCart(newCart, shop);
    } else {
      saveCart([...cart, cartItem], shop);
    }
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    saveCart(newCart, shopSlug);
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    const newCart = cart.map(item => 
      item.id === productId ? { ...item, quantity } : item
    );
    saveCart(newCart, shopSlug);
  };

  const clearCart = () => {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartShop');
    setCart([]);
    setShopSlug(null);
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.final_price * item.quantity), 0);
  };

  const getItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cart,
    shopSlug,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotal,
    getItemCount,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
